// ############################################################################
// ## VOID UTILS ##############################################################
// ############################################################################

import $ from "jquery";
import * as _ from "./framework/voidash";
import {encodeQueryString, decodeQueryString} from "./framework/utils";
import {$doc} from "./_globals.js";

export {
  debounce,
  throttle,
  throttleFrame as throttleNative,
  arrayRemove as removeFromArray,
  noop
} from "./framework/voidash";

export {encodeQueryString, decodeQueryString, toggleClass, htmlEntities} from "./framework/utils";

export function alterQueryString(url, ...overrides) {
  const a = document.createElement("a");
  a.href = url;
  const params = decodeQueryString(a.search);
  for (const newParams of overrides) {
    for (const key of _.keys(newParams)) {
      if (newParams[key] == null) delete params[key];
      else params[key] = newParams[key];
    }
  }
  const queryString = encodeQueryString(params);
  a.search = queryString === "?" ? "" : queryString;
  return a.href;
}

export function cacheBust(url) {
  return alterQueryString(url, { [Math.random().toFixed(5).substring(2)]: true });
}

export function hasClass(el, className) {
  if(typeof el === "string") el = "#" + el;
  return $(el).hasClass(className);
}

export function addClass(el, className) {
  if(typeof el === "string") el = "#" + el;
  $(el).addClass(className);
}

export function removeClass(el, className) {
  if(typeof el === "string") el = "#" + el;
  $(el).removeClass(className);
}

function getElement(el) { return typeof el === "string" ? document.getElementById(el) : el; }

export var requestFullscreen = _.noop, exitFullscreen = _.noop, isFullscreen = function() { return false; };

if(document.exitFullscreen) { // W3C API
  requestFullscreen = function(el) { return getElement(el).requestFullscreen(); };
  exitFullscreen = function() { if(isFullscreen()) document.exitFullscreen(); };
  isFullscreen = function(el) {
    if(el) return document.fullscreenElement === el;
    return !!document.fullscreenElement;
  };
}

// number formatting
const formatNumberDefault = {
  order: ",",
  decimal: ".",
  precision: 0,
  postfix: ""
};

export function formatNumber(val, opts) {
  opts = _.assign({}, formatNumberDefault, opts);
  var sign = "";
  if (val < 0) {
    val = - val;
    sign = "";
  }
  val = +val.toFixed(opts.precision); // round
  var int = Math.floor(val);
  var fract = val - int;
  val = ''+int;
  var arr = val.split("").reverse();
  var out = [];$
  var j = 0;
  for (var i=0;i<arr.length;i++) {
    out.unshift(arr[i]);
    var isDot = arr[i+1] === ".";
    if ((j%3)===2 && i!==arr.length-1 && !isDot) out.unshift(opts.order);
    if(!isDot) j++;
  }
  val=out.join("")+ (fract? (opts.decimal+(''+fract.toFixed(2)).substring(2)):'');
  return sign+val+opts.postfix;
}

// custom toggle class on scroll for menu and scroll to top arrow up
export function toggleClassOnScroll(selector, className, offset) {
  var $win = $(window);
  var $el = $(selector);
  var wasOver = false;

  function onScroll() {
    var over = $win.scrollTop() > offset;
    if (over !== wasOver) $el.toggleClass(className, over);
    wasOver = over;
  }

  $win.scroll(onScroll);
  onScroll();
}

export function drag(opts) {
  var startX, startY;
  var button = opts.button || 0;

  function getMouseArgs(evt) {
    return {
      x: evt.clientX,
      y: evt.clientY,
      startX: startX,
      startY: startY,
      deltaX: evt.clientX - startX,
      deltaY: evt.clientY - startY,
      evt
    };
  }

  function getTouchArgs(evt) {
    var touch = evt.touches[0];
    return {
      x: touch && touch.clientX,
      y: touch && touch.clientY,
      startX: startX,
      startY: startY,
      deltaX: touch && (touch.clientX - startX),
      deltaY: touch && (touch.clientY - startY)
    };
  }

  function mousedown(evt) {
    if ($(evt.target).closest(":button").length) return;

    if (evt.button !== button) return;

    startX = evt.clientX;
    startY = evt.clientY;

    if(opts.onStart) opts.onStart(getMouseArgs(evt), evt);

    document.addEventListener("mousemove", mousemove);
    document.addEventListener("mouseup", mouseup);

    evt.preventDefault();
  }

  function mousemove(evt) {
    opts.onMove(getMouseArgs(evt), evt);
  }

  function mouseup(evt) {
    if(opts.onEnd) opts.onEnd(getMouseArgs(evt), evt);

    document.removeEventListener("mousemove", mousemove);
    document.removeEventListener("mouseup", mouseup);
  }

  var touchInProgress = false;

  function touchstart(evt) {
    if ($(evt.target).closest(":button").length) return;

    if(touchInProgress) return;
    touchInProgress = true;

    startX = evt.touches[0].clientX;
    startY = evt.touches[0].clientY;

    if(opts.onStart) opts.onStart(getTouchArgs(evt), evt);

    document.addEventListener("touchmove", touchmove);
    document.addEventListener("touchend", touchend);
    document.addEventListener("touchcancel", touchend);

    evt.preventDefault();
  }

  function touchmove(evt) {
    opts.onMove(getTouchArgs(evt), evt);
  }

  function touchend(evt) {
    touchInProgress = false;

    if(opts.onEnd) opts.onEnd(getTouchArgs(evt), evt);

    document.removeEventListener("touchmove", touchmove);
    document.removeEventListener("touchend", touchend);
    document.removeEventListener("touchcancel", touchend);
  }

  function bind() {
    opts.el.addEventListener("mousedown", mousedown);
    opts.el.addEventListener("touchstart", touchstart);
  }

  function unbind() {
    opts.el.removeEventListener("mousedown", mousedown);
    opts.el.removeEventListener("touchstart", touchstart);
  }

  bind();

  return { bind, unbind };
}

export function pinch(opts) {
  const ids = [];
  const coords = {};
  let listening = false;
  let pinching = false;
  let startDist;
  let factor;
  let startFactor;

  const getTouchId = _.property("identifier");

  function updateTouchCoords(touches) {
    _.assign(coords, _.fromPairs(_.map(touches, t => [getTouchId(t), {x: t.clientX, y: t.clientY}])));
  }

  function getDist() {
    const xy0 = coords[ids[0]];
    const xy1 = coords[ids[1]];
    const dx = xy0.x - xy1.x;
    const dy = xy0.y - xy1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function start(evt) {
    const touches = evt.changedTouches;
    ids.push(..._.map(touches, getTouchId));
    updateTouchCoords(touches);

    if (ids.length >= 2 && !pinching) {
      startDist = getDist();
      startFactor = 1;
      if (opts.onStart) {
        opts.onStart({
          xy: ids.slice(0, 2).map(_.propertyOf(coords))
        }, evt);
      }
      pinching = true;
    }

    if (!listening) {
      document.addEventListener("touchmove", move);
      document.addEventListener("touchend", end);
      document.addEventListener("touchcancel", end);
      document.addEventListener("touchleave", end);
      listening = true;
    }

    evt.preventDefault();
  }

  function move(evt) {
    updateTouchCoords(evt.changedTouches);
    if (pinching) {
      const dist = getDist();
      factor = startFactor * dist / startDist;
      if (opts.onMove) opts.onMove({factor}, evt);
    }
  }

  function end(evt) {
    const touches = evt.changedTouches;
    updateTouchCoords(touches);

    const drivingTouches = ids.slice(0, 2);
    let drivingTouchesChanged = false;

    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i];
      const id = getTouchId(touch);
      const idx = ids.indexOf(id);
      if (idx >= 0) {
        ids.splice(idx, 1);
        delete coords[id];
        drivingTouchesChanged = drivingTouchesChanged || _.contains(drivingTouches, id);
      }
    }

    if (ids.length === 0) {
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", end);
      document.removeEventListener("touchcancel", end);
      document.removeEventListener("touchleave", end);
      listening = false;
    }

    if (ids.length < 2) {
      if (pinching) {
        if (opts.onEnd) opts.onEnd({factor}, evt);
        pinching = false;
      }
    } else if (drivingTouchesChanged) {
      startDist = getDist();
      startFactor = factor;
    }
  }

  function bind() {
    opts.el.addEventListener("touchstart", start);
  }

  function unbind() {
    opts.el.removeEventListener("touchstart", start);
  }

  bind();

  return {bind, unbind};
}

export function ajax(opts) {
  var xhr = new XMLHttpRequest();

  // avoid cache
  var url = alterQueryString(opts.url, {
    ajaxRand: Math.random().toFixed(5).substr(2)
  });

  xhr.open(opts.method, url);

  xhr.onreadystatechange = function() {
    if(this.readyState === XMLHttpRequest.DONE) {
      if(this.status === 200) {
        if(opts.success) opts.success(this.responseText);
      } else {
        if(opts.fail) opts.fail(this.status, this.statusText);
      }
    }
  };

  xhr.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
  xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

  xhr.send(opts.data);

  return xhr;
}

// ajax validation
const createAJAXWarningHandlerDefaultOpts = {
  focusFirstInvalid: true,
  validateUnchanged: true,
  wasValidatedClassName: "was-validated",
  inputWasValidatedClassName: null,
  retainValidityOnChange: false
};

export function createAJAXWarningHandler(sel, opts) {
  opts = _.assign({}, createAJAXWarningHandlerDefaultOpts, opts);
  const origTexts = {};

  function setContent(name, content) {
    if (opts.predicate && !opts.predicate(name)) return;

    const $form = $(sel);
    const $input = $form.find("[name='" + name + "']");
    if (opts.inputWasValidatedClassName) $input.addClass(opts.inputWasValidatedClassName);
    const $msgEl = $input.nextAll(".invalid-feedback,.invalid-tooltip").first();
    if(content !== null) {
      if(!origTexts.hasOwnProperty(name)) origTexts[name] = $msgEl.text();
      $input.each(function(i, el) {
        el.setCustomValidity(content);
      });
      $msgEl.text(content);
    } else {
      $input.each(function(i, el) {el.setCustomValidity("");});
      $msgEl.text(origTexts[name]);
      delete origTexts[name];
    }
  }

  if (!opts.retainValidityOnChange) {
    $doc.on("change", sel, function ($evt) {
      setContent($evt.target.name, null);
    });
  }

  return function(props, res) {
    if(!res) res = props;
    const arr = res.warnings || [];
    const warnings = {};

    arr.forEach(pair => {
      let br = "<br>";
      if(!warnings.hasOwnProperty(pair[0])) {
        warnings[pair[0]] = "";
        br = "";
      }
      warnings[pair[0]] += br + pair[1];
    });

    _.forEach(warnings, (v, k) => { setContent(k, v); })

    const prevNames = Object.keys(origTexts);
    let prevLength = prevNames.length;
    prevNames.forEach(prevName => {
      if (!warnings.hasOwnProperty(prevName)) {
        setContent(prevName, null);
        prevLength--;
      }
    });

    if(prevLength > 0) {
      $(sel).addClass(opts.wasValidatedClassName);
      if (opts.focusFirstInvalid) {
        $(sel + " :invalid").first().focus();
      }
      return false;
    } else {
      $(sel).removeClass(opts.wasValidatedClassName);
      return true;
    }
  }
}

export function ajaxValidation(sel) {
  AJAX.addIdCallback($(sel).get(0), createAJAXWarningHandler(sel));
}

export function parseDateString(str) {
  var parts = str.split(/[ T]/, 2);

  var dateParts = parts[0].split("-");
  var year = +dateParts[0];
  var month = +dateParts[1] - 1;
  var date = +dateParts[2];

  var hours, minutes, seconds;

  if(parts.length === 2) {
    var timeParts = parts[1].split(":");
    hours = +timeParts[0];
    minutes = +(timeParts[1] || 0);
    seconds = +(timeParts[2] || 0);
  }

  return new Date(year, month, date, hours, minutes, seconds);
}

const easings = {
  linear: x => x,
  easeIn: x => x*x*x,
  easeOut: x => {
    const x2 = 1 - x;
    return 1 - x2*x2*x2;
  },
  easeInX: x => x*x*x*x*x,
  easeOutX: x => {
    const x2 = 1 - x;
    return 1 - x2*x2*x2*x2*x2;
  },
};

export function animate(opts) {
  const defaults = {
    duration: 1000,
    easing: "linear",
    from: 0,
    to: 1
  };

  const {duration, easing, from, to, callback} = _.assign({}, defaults, opts);

  if (!callback) {
    console.warn("Animate: no callback specified");
    return;
  }

  const ease = easings[easing];

  if (!ease) {
    console.warn("Animate: invalid easing: " + easing);
    return;
  }

  const startTime = Date.now();

  function tick() {
    const time = Date.now();
    const linVal = (time - startTime) / duration;
    if (linVal >= 1) {
      callback(to);
    } else {
      const mappedVal = from + ease(linVal) * (to - from);
      callback(mappedVal);
      requestAnimationFrame(tick);
    }
  }

  requestAnimationFrame(tick);
}

export function PHP_serialize (mixedValue) {
  //  discuss at: http://locutus.io/php/serialize/
  // original by: Arpad Ray (mailto:arpad@php.net)
  // improved by: Dino
  // improved by: Le Torbi (http://www.letorbi.de/)
  // improved by: Kevin van Zonneveld (http://kvz.io/)
  // bugfixed by: Andrej Pavlovic
  // bugfixed by: Garagoth
  // bugfixed by: Russell Walker (http://www.nbill.co.uk/)
  // bugfixed by: Jamie Beck (http://www.terabit.ca/)
  // bugfixed by: Kevin van Zonneveld (http://kvz.io/)
  // bugfixed by: Ben (http://benblume.co.uk/)
  // bugfixed by: Codestar (http://codestarlive.com/)
  // bugfixed by: idjem (https://github.com/idjem)
  //    input by: DtTvB (http://dt.in.th/2008-09-16.string-length-in-bytes.html)
  //    input by: Martin (http://www.erlenwiese.de/)
  //      note 1: We feel the main purpose of this function should be to ease
  //      note 1: the transport of data between php & js
  //      note 1: Aiming for PHP-compatibility, we have to translate objects to arrays
  //   example 1: serialize(['Kevin', 'van', 'Zonneveld'])
  //   returns 1: 'a:3:{i:0;s:5:"Kevin";i:1;s:3:"van";i:2;s:9:"Zonneveld";}'
  //   example 2: serialize({firstName: 'Kevin', midName: 'van'})
  //   returns 2: 'a:2:{s:9:"firstName";s:5:"Kevin";s:7:"midName";s:3:"van";}'
  //   example 3: serialize( {'ü': 'ü', '四': '四', '𠜎': '𠜎'})
  //   returns 3: 'a:3:{s:2:"ü";s:2:"ü";s:3:"四";s:3:"四";s:4:"𠜎";s:4:"𠜎";}'

  var val, key, okey
  var ktype = ''
  var vals = ''
  var count = 0

  var _utf8Size = function (str) {
    return ~-encodeURI(str).split(/%..|./).length
  }

  var _getType = function (inp) {
    var match
    var key
    var cons
    var types
    var type = typeof inp

    if (type === 'object' && !inp) {
      return 'null'
    }

    if (type === 'object') {
      if (!inp.constructor) {
        return 'object'
      }
      cons = inp.constructor.toString()
      match = cons.match(/(\w+)\(/)
      if (match) {
        cons = match[1].toLowerCase()
      }
      types = ['boolean', 'number', 'string', 'array']
      for (key in types) {
        if (cons === types[key]) {
          type = types[key]
          break
        }
      }
    }
    return type
  }

  var type = _getType(mixedValue)

  switch (type) {
    case 'function':
      val = ''
      break
    case 'boolean':
      val = 'b:' + (mixedValue ? '1' : '0')
      break
    case 'number':
      val = (Math.round(mixedValue) === mixedValue ? 'i' : 'd') + ':' + mixedValue
      break
    case 'string':
      val = 's:' + _utf8Size(mixedValue) + ':"' + mixedValue + '"'
      break
    case 'array':
    case 'object':
      val = 'a'
      /*
      if (type === 'object') {
        var objname = mixedValue.constructor.toString().match(/(\w+)\(\)/);
        if (objname === undefined) {
          return;
        }
        objname[1] = serialize(objname[1]);
        val = 'O' + objname[1].substring(1, objname[1].length - 1);
      }
      */

      for (key in mixedValue) {
        if (mixedValue.hasOwnProperty(key)) {
          ktype = _getType(mixedValue[key])
          if (ktype === 'function') {
            continue
          }

          okey = (key.match(/^[0-9]+$/) ? parseInt(key, 10) : key)
          vals += PHP_serialize(okey) + PHP_serialize(mixedValue[key])
          count++
        }
      }
      val += ':' + count + ':{' + vals + '}'
      break
    case 'undefined':
    default:
      // Fall-through
      // if the JS object has a property which contains a null value,
      // the string cannot be unserialized by PHP
      val = 'N'
      break
  }
  if (type !== 'object' && type !== 'array') {
    val += ';'
  }

  return val
}
